import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, Sparkles, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { COMPLAINTS, NEEDS } from "@/data/content";

export default function Book() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [specialties, setSpecialties] = useState([]);
  const [complaint, setComplaint] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [need, setNeed] = useState("");
  const [doctors, setDoctors] = useState(null);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const preferredDoctor = params.get("doctor") || "";

  useEffect(() => { api.get("/public/specialties").then(r => setSpecialties(r.data.specialties || [])); }, []);

  // Preselect a specialty coming from a specialty/doctor page and show matches immediately.
  useEffect(() => {
    const sp = params.get("specialty");
    if (sp) { setSpecialty(sp); runFind({ specialty: sp }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runTriage = async () => {
    if (aiText.trim().length < 4) { toast("Please describe your symptoms first"); return; }
    setAiBusy(true); setAiResult(null);
    try {
      const { data } = await api.post("/triage", { symptom: aiText });
      setAiResult(data);
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || "AI triage is unavailable right now.");
    } finally { setAiBusy(false); }
  };
  const applySuggestion = (sp) => { setSpecialty(sp); setComplaint(complaint || aiText.slice(0, 80)); toast(`Specialty set to ${sp}`); };

  const runFind = async (override = {}) => {
    const { data } = await api.get(`/public/match`, { params: { complaint, specialty, need, ...override } });
    setDoctors(data.doctors || []);
  };
  const find = () => runFind();

  const book = (doctor) => {
    sessionStorage.setItem("booking", JSON.stringify({ complaint, specialty, need, doctor }));
    nav("/patient");
  };

  return (
    <section className="pgwrap">
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <span className="pg-eyebrow">Book a consultation</span>
        <h2 className="pg-h2">Let’s find the right specialist</h2>
        <p className="pg-lead" style={{ margin: "0 auto" }}>Answer three quick questions and we’ll match you with verified specialists for your case.</p>
      </div>

      <div className="bookcard">
        <div className="ai-triage" data-testid="ai-triage">
          <div className="ai-triage-head">
            <span className="ai-triage-icon"><Sparkles size={16} /></span>
            <div>
              <h4>Not sure which specialist you need?</h4>
              <p>Describe your symptoms and our AI will suggest the right specialty.</p>
            </div>
          </div>
          <textarea className="bk-select ai-triage-input" rows={2} value={aiText}
            onChange={e => setAiText(e.target.value)}
            placeholder="e.g. persistent headache with blurred vision for two weeks…"
            data-testid="ai-triage-input" />
          <button className="pg-btn pg-btn-ink" onClick={runTriage} disabled={aiBusy} data-testid="ai-triage-btn">
            <Sparkles size={15} /> {aiBusy ? "Analysing…" : "Suggest a specialist"}
          </button>
          {aiResult && (
            <div className="ai-triage-result" data-testid="ai-triage-result">
              {aiResult.urgent && (
                <div className="ai-urgent" data-testid="ai-triage-urgent">
                  <AlertTriangle size={15} /> Your symptoms may need urgent care. If this is an emergency, please call your local emergency number now.
                </div>
              )}
              <p className="ai-rec">Recommended: <b>{aiResult.specialty}</b></p>
              <p className="ai-reason">{aiResult.reason}</p>
              <div className="ai-chips">
                <button className="ai-chip primary" onClick={() => applySuggestion(aiResult.specialty)} data-testid="ai-triage-use">
                  Use {aiResult.specialty}
                </button>
                {(aiResult.alternatives || []).map(a => (
                  <button className="ai-chip" key={a} onClick={() => applySuggestion(a)} data-testid={`ai-triage-alt-${a}`}>{a}</button>
                ))}
              </div>
              <p className="ai-disclaimer">This is an AI suggestion, not a medical diagnosis.</p>
            </div>
          )}
        </div>

        <div className="bk-field">
          <label>What are your complaints?</label>
          <select className="bk-select" value={complaint} onChange={e => setComplaint(e.target.value)} data-testid="book-complaint">
            <option value="">Select your main concern…</option>
            {COMPLAINTS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="bk-field">
          <label>Choose your specialty</label>
          <select className="bk-select" value={specialty} onChange={e => setSpecialty(e.target.value)} data-testid="book-specialty">
            <option value="">Select a specialty…</option>
            {specialties.map(s => <option key={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="bk-field">
          <label>What do you need?</label>
          <select className="bk-select" value={need} onChange={e => setNeed(e.target.value)} data-testid="book-need">
            <option value="">Select what you need…</option>
            {NEEDS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <button className="pg-btn pg-btn-green pg-btn-lg" style={{ width: "100%" }} onClick={find} disabled={!complaint || !specialty || !need} data-testid="book-find">
          <Search size={17} /> Find my specialist
        </button>
        <p className="bk-note">The list of specialists is decided by our matching service based on your answers.</p>

        {doctors && (
          <div className="bk-results" data-testid="book-results">
            {doctors.length === 0 && <p className="bk-note">No specialists listed yet for this area — please check back soon.</p>}
            {doctors.map(d => (
              <div className={`docrow ${preferredDoctor === d.id ? "docrow-pref" : ""}`} key={d.id}>
                <div className="av">{d.name.replace("Dr. ", "").charAt(0)}</div>
                <div className="m">
                  <h4>{d.name} {preferredDoctor === d.id && <span className="pref-badge">Selected</span>}</h4>
                  <p>{d.specialty} · {d.reg_no}</p>
                </div>
                <button className="pg-btn pg-btn-green" onClick={() => book(d)} data-testid={`book-doctor-${d.id}`}>
                  Book <ArrowRight size={15} />
                </button>
              </div>
            ))}
            <div className="bk-hint">You’ll sign in or create a patient account to confirm your booking securely.</div>
          </div>
        )}
      </div>
    </section>
  );
}
